import React from 'react';
import { observer } from 'mobx-react-lite';
import { DownloadIcon } from '@radix-ui/react-icons';
import { IconButton } from '@producer/ui-kit';

import { api } from '../../../api';
import { DropdownMenu } from '../../../components/modals/dropdown-menu/DropdownMenu';
import { catchError } from '../../../core/catch-error';

import type { CrewFilter } from '../lib/filter-crew';

export const buildCrewExportUrl = (
  projectId: string,
  format: 'csv' | 'pdf',
  filter: CrewFilter = {},
): string => {
  const params = new URLSearchParams({ format });

  // Empty values are omitted rather than sent blank, so the server never filters on ''.
  // `department`/`team` are multi-select (string[]): append one param per value rather than
  // `params.set` once, so `?department=Camera&department=Sound` reaches the server as a repeated
  // param — see `apps/api/src/project/crew/controllers/export-crew.query.ts`.
  for (const [key, value] of Object.entries(filter)) {
    if (Array.isArray(value)) {
      for (const item of value) {
        if (item) params.append(key, item);
      }
    } else if (value) {
      params.set(key, value);
    }
  }

  return `/projects/${projectId}/crew/export?${params.toString()}`;
};

type CrewExportMenuProps = {
  projectId: string;
  /** The screen's active filter. Passing it is what keeps the export honest. */
  filter?: CrewFilter;
};

export const CrewExportMenu: React.FC<CrewExportMenuProps> = observer(({ projectId, filter }) => {
  const download = async (format: 'csv' | 'pdf') => {
    try {
      // Goes through `api` so the auth token is attached; responseType blob because the body is
      // a file, not JSON.
      const { data, headers } = await api.get(buildCrewExportUrl(projectId, format, filter), {
        responseType: 'blob',
      });

      const href = URL.createObjectURL(new Blob([data], { type: headers['content-type'] }));

      const link = document.createElement('a');
      link.href = href;
      link.download = `crew.${format}`;
      link.click();

      URL.revokeObjectURL(href);
    } catch (e) {
      catchError(e);
    }
  };

  return (
    <DropdownMenu
      size="large"
      trigger={<IconButton icon={<DownloadIcon />} aria-label="Export crew list" />}
      items={[
        { title: 'Export as CSV', onSelect: () => download('csv') },
        { title: 'Export as PDF', onSelect: () => download('pdf') },
      ]}
    />
  );
});
